import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicQuote from "@/pages/PublicQuote";
import PublicAppointment from "@/pages/PublicAppointment";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerForm from "@/pages/CustomerForm";
import Products from "@/pages/Products";
import ProductForm from "@/pages/ProductForm";
import ImportCatalog from "@/pages/ImportCatalog";
import MasterDataImport from "@/pages/MasterDataImport";
import ScanCenter from "@/pages/ScanCenter";
import Quotes from "@/pages/Quotes";
import QuoteEditor from "@/pages/QuoteEditor";
import QuoteDetail from "@/pages/QuoteDetail";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Suppliers from "@/pages/Suppliers";
import Inventory from "@/pages/Inventory";
import Purchasing from "@/pages/Purchasing";
import Jobs from "@/pages/Jobs";
import AutomotiveVehicles from "@/pages/AutomotiveVehicles";
import TireStorage from "@/pages/TireStorage";
import HistoricalInvoiceImport from "@/pages/HistoricalInvoiceImport";
import Settings from "@/pages/Settings";
import EInvoices from "@/pages/finance/EInvoices";
import EInvoiceEditor from "@/pages/finance/EInvoiceEditor";
import EInvoiceDetail from "@/pages/finance/EInvoiceDetail";
import Inbound from "@/pages/finance/Inbound";
import Datev from "@/pages/finance/Datev";
import Banking from "@/pages/finance/Banking";
import Dunning from "@/pages/finance/Dunning";
import BuyingGroup from "@/pages/BuyingGroup";
import HelpCenter from "@/pages/HelpCenter";
import { ModuleGate } from "@/components/common/ModuleGate";
import PlannedModule from "@/pages/PlannedModule";
import PosStation from "@/pages/PosStation";
import ShopAdmin from "@/pages/shop/ShopAdmin";
import Storefront from "@/pages/storefront/Storefront";
import StorefrontProduct from "@/pages/storefront/StorefrontProduct";
import ShopCart from "@/pages/storefront/ShopCart";
import ShopOrderConfirmation from "@/pages/storefront/ShopOrderConfirmation";
import ShopAccount from "@/pages/storefront/ShopAccount";
import ShopSetPassword from "@/pages/storefront/ShopSetPassword";
import ControlApp from "@/pages/control/ControlApp";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex h-screen items-center justify-center" data-testid="app-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/login" replace />;
  return children;
}

// Platform admins without a tenant have no dashboard; send them to platform admin.
function Home() {
  const { platformRole, activeTenantId, tenantRole } = useAuth();
  if (platformRole && !activeTenantId) return <Navigate to="/platform" replace />;
  if (tenantRole === "field_service") return <Navigate to="/jobs" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/q/:token" element={<PublicQuote />} />
          <Route path="/appointment/:token" element={<PublicAppointment />} />
          <Route path="/pos/station" element={<PosStation />} />
          <Route path="/shop/:slug" element={<Storefront />} />
          <Route path="/shop/:slug/cart" element={<ShopCart />} />
          <Route path="/shop/:slug/order/:payToken" element={<ShopOrderConfirmation />} />
          <Route path="/shop/:slug/account" element={<ShopAccount />} />
          <Route path="/shop/:slug/set-password" element={<ShopSetPassword />} />
          <Route path="/shop/:slug/p/:productId" element={<StorefrontProduct />} />
          <Route path="/control/*" element={<ControlApp />} />
          <Route path="/platform" element={<Navigate to="/control" replace />} />
          <Route
            path="/"
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route index element={<Home />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/new" element={<CustomerForm />} />
            <Route path="customers/:id" element={<CustomerForm />} />
            <Route path="products" element={<Products />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:id" element={<ProductForm />} />
            <Route path="imports" element={<ImportCatalog />} />
            <Route path="data-import/:kind" element={<MasterDataImport />} />
            <Route path="scan" element={<ModuleGate module="inventory_scan" perm="inventory:read" moduleKey="scan"><ScanCenter /></ModuleGate>} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="quotes/new" element={<QuoteEditor />} />
            <Route path="quotes/:id/edit" element={<QuoteEditor />} />
            <Route path="quotes/:id" element={<QuoteDetail />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="purchasing" element={<ModuleGate module="purchasing" moduleKey="purchasing"><Purchasing /></ModuleGate>} />
            <Route path="buying-group" element={<ModuleGate module="buying_group_connector" perm="purchasing:read" moduleKey="buying_group_connector"><BuyingGroup /></ModuleGate>} />
            <Route path="jobs" element={<ModuleGate module="service" moduleKey="jobs"><Jobs /></ModuleGate>} />
            <Route path="vehicles" element={<ModuleGate module="automotive_workshop" perm="customers:read" moduleKey="vehicles"><AutomotiveVehicles /></ModuleGate>} />
            <Route path="vehicles/:id" element={<ModuleGate module="automotive_workshop" perm="customers:read" moduleKey="vehicles"><AutomotiveVehicles /></ModuleGate>} />
            <Route path="tire-storage" element={<ModuleGate module="automotive_workshop" perm="inventory:read" moduleKey="tire_storage"><TireStorage /></ModuleGate>} />
            <Route path="data-import/historical-invoices" element={<ModuleGate module="automotive_workshop" perm="einvoice:create" moduleKey="historical_invoices"><HistoricalInvoiceImport /></ModuleGate>} />
            <Route path="finance/einvoices" element={<ModuleGate module="einvoice_datev" moduleKey="einvoices"><EInvoices /></ModuleGate>} />
            <Route path="finance/einvoices/new" element={<ModuleGate module="einvoice_datev" moduleKey="einvoices"><EInvoiceEditor /></ModuleGate>} />
            <Route path="finance/einvoices/:id/edit" element={<ModuleGate module="einvoice_datev" moduleKey="einvoices"><EInvoiceEditor /></ModuleGate>} />
            <Route path="finance/einvoices/:id" element={<ModuleGate module="einvoice_datev" moduleKey="einvoices"><EInvoiceDetail /></ModuleGate>} />
            <Route path="finance/dunning" element={<ModuleGate module="einvoice_datev" perm="einvoice:view" moduleKey="dunning"><Dunning /></ModuleGate>} />
            <Route path="finance/inbound" element={<ModuleGate module="einvoice_datev" moduleKey="inbound"><Inbound /></ModuleGate>} />
            <Route path="finance/datev" element={<ModuleGate module="einvoice_datev" moduleKey="datev"><Datev /></ModuleGate>} />
            <Route path="finance/banking" element={<ModuleGate module="banking" perm="banking:view" moduleKey="banking"><Banking /></ModuleGate>} />
            <Route path="stock" element={<PlannedModule moduleKey="stock" />} />
            <Route path="invoices" element={<PlannedModule moduleKey="invoices" />} />
            <Route path="reports" element={<PlannedModule moduleKey="reports" />} />
            <Route path="documents" element={<PlannedModule moduleKey="documents" />} />
            <Route path="shop-admin" element={<ModuleGate module="online_shop" perm="shop:read" moduleKey="shop"><ShopAdmin /></ModuleGate>} />
            <Route path="settings" element={<Settings />} />
            <Route path="help" element={<HelpCenter />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
